/**
 * A 'Composite Key' is key created by combining different key-values pairs
 * to uniquely identify a data set. For example, if a report has data for
 * snapshotId=live, timeFrame=Sep-2020 and marketplace=US the composite key to represent
 * this data set would be '[marketplace=US|snapshotId=live|timeframe=Sep-2020]'
 * In the same manner, if a report has two different data sets -
 * their corresponding composite keys are going to be as below -
 * - snapshotId=live, marketplace=1, timeFrame=09/2020 => [marketplace=1|snapshotId=live|timeframe=09/2020]
 * - snapshotId=live, marketplace=4, timeFrame=08/2020 => [marketplace=4|snapshotId=live|timeframe=08/2020]
 * - snapshotId=live, marketplace=1 => [marketplace=1|snapshotId=live]
 * - snapshotId=live => [snapshotId=live]
 *
 * As shown above, each key value pair is separated with a '|' sign and in each pair there
 * is an '=' sign between the key and the value. There can be one or more key-value pairs
 * in a composite key.
 *
 * The column keys of a report are joined with the composite key -
 * i.e. '[marketplace=1|snapshotId=live|timeframe=08/2020]@firstAnswer'
 * So if a report has multiple 'firstAnswer' columns coming from different data sets
 * then the columns can be separated using this composite key.
 *
 * This file contains methods to handle composite key related operations.
 */

import { KEY_SEPARATOR, SNAPSHOT_ID } from '../common/constants';
import { formatDate, formatMarketplace } from './formatting';
import { REPORT_REQUEST_PAYLOAD } from '../../../constants/payload';

export const COMPOSITE_KEY_SEPARATOR = '|';
export const COMPOSITE_KEY_EQUALS = '=';
export const COMPOSITE_KEY_JOIN = '/';

/**
 * Extracts the column key portion from a report key that can start with a composite key.
 * If it doesn't start with a composite key, the original input string will be returned.
 * Example -
 *  '[snapshotId=live]@firstAnswer' returns 'firstAnswer'
 */
export const extractColumnKey = (reportKey) => {
  return reportKey.includes(KEY_SEPARATOR) ? reportKey.split(KEY_SEPARATOR)[1] : reportKey;
};

/**
 * Extracts the composite key portion from a report key that can start with a composite key.
 * If it doesn't start with a composite key, the original input string will be returned.
 * Example -
 *  '[snapshotId=live]@firstAnswer', keepSeparator=false returns '[snapshotId=live]'
 *  '[snapshotId=live]@firstAnswer', keepSeparator=true returns '[snapshotId=live]@'
 */
export const extractColumnKeyPrefix = (reportKey, keepSeparator = true) => {
  const separator = keepSeparator ? KEY_SEPARATOR : '';
  return reportKey.includes(KEY_SEPARATOR) ? reportKey.split(KEY_SEPARATOR)[0] + separator : reportKey;
};

/**
 * Given a the composite key of a column heading, derives the composite key
 * in a format that is suitable for display in the report heading. This method
 * takes care of converting dates to the right display format and marketplace ids
 * to the corresponding ISO code (i.e. US, SG)
 * * Example -
 *  '[snapshotId=live]@firstAnswer' returns '[live]'
 *  '[marketplace=1]@firstAnswer' returns '[US]'
 *  '[marketplace=1|timeframe=09/2020]@firstAnswer' returns '[US_Sep-2020]'
 *
 */
export const deriveHeadingDisplayKey = (headingCompositeKey, allCompositeKeys = []) => {
  let key = headingCompositeKey.replace('[', '').replace(']', '');
  const parts = key.split(COMPOSITE_KEY_SEPARATOR);
  let displayKeys = [];

  // Only skip snapshot if all composite keys share the same snapshot (i.e. single snapshot selected)
  const uniqueSnapshots = allCompositeKeys.length > 0
    ? [...new Set(allCompositeKeys.map(k => {
        const match = k.match(/snapshotId=([^|\\]]*)/);
        return match ? match[1] : null;
      }).filter(Boolean))]
    : [];
  const singleSnapshot = uniqueSnapshots.length <= 1;

  for (let p of parts) {
    const keyParts = p.split(COMPOSITE_KEY_EQUALS);
    const shouldIgnore = singleSnapshot && parts.length > 1 && keyParts[0] === SNAPSHOT_ID;
    if (shouldIgnore) {
      continue;
    }

    let displayKey = keyParts[1];

    if (keyParts[0] === REPORT_REQUEST_PAYLOAD.TIMEFRAME) {
      displayKey = formatDate(keyParts[1], true, ' ');
    }

    if (keyParts[0] === REPORT_REQUEST_PAYLOAD.MARKETPLACE) {
      displayKey = formatMarketplace(keyParts[1]);
    }

    if (displayKey) {
      displayKeys.push(displayKey);
    }
  }

  return displayKeys.length > 0 ? `[${displayKeys.join(COMPOSITE_KEY_JOIN)}]` : '';
};

/**
 * Given a list of keys, returns the set of unique composite keys.
 */
export const getUniqueCompositeKeys = (keys) => {
  let compositeKeys = [];
  for (let k of keys) {
    if (k.includes(KEY_SEPARATOR)) {
      const parts = k.split(KEY_SEPARATOR);
      compositeKeys.push(parts[0]);
    }
  }

  return [...new Set(compositeKeys)];
};

/**
 * Adds composite key to an item.
 */
export const addCompositeKey = (item, compositeKey) => {
  return compositeKey + KEY_SEPARATOR + item;
};

/**
 * Adds composite keys to each item of an accuracy list.
 */
export const addCompositeKeys = (accuracyList, sortedKeys, compositeKey) => {
  return accuracyList.map((item) => {
    return sortedKeys
      .map((k) => {
        return { [`${compositeKey}${KEY_SEPARATOR}${k}`]: item[k] };
      })
      .reduce((acc, item) => {
        return { ...acc, ...item };
      }, {});
  });
};

/**
 * Adds composite keys to each item of a summary map (used in summary report).
 */
export const addCompositeKeysToSummaryMap = (summaryMap, compositeKey) => {
  return Object.keys(summaryMap).map((key) => {
    const item = summaryMap[key];
    return {
      [key]: Object.keys(item)
        .map((k) => {
          return { [`${compositeKey}${KEY_SEPARATOR}${k}`]: item[k] };
        })
        .reduce((acc, item) => {
          return { ...acc, ...item };
        }, {}),
    };
  });
};

/**
 *  Adds composite keys to each item in a list of items.
 */
export const getForEachCompositeKey = (items, compositeKeys) => {
  const uniqueCompositeKeys = [...new Set(compositeKeys)];
  let result = [];
  for (let item of items) {
    result.push(...uniqueCompositeKeys.map((k) => `${k}${KEY_SEPARATOR}${item}`));
  }

  return result;
};

/**
 *  Adds composite key to current Key.
 */
 export const concatWithCompositeKey = (key, compositeKey) => {
   return `${compositeKey}${KEY_SEPARATOR}${key}`
 }

/**
 * Gets the composite keys for a given list of request payloads.
 */
export const getCompositeKeys = (requestPayloads, dataSetKeys) => {
  return requestPayloads.map((p) => getCompositeKey(p, dataSetKeys));
};

const getCompositeKey = (requestPayload, dataSetKeys) => {
  const compositeKeys = dataSetKeys.sort().map((k) => {
    return `${k}${COMPOSITE_KEY_EQUALS}${getPayloadValue(requestPayload, k)}`;
  });

  return `[${compositeKeys.join(COMPOSITE_KEY_SEPARATOR)}]`;
};

/**
 * Find the matched compositeKey
 * item: "1"
 * compositeKeys: ['marketplace=1|snapshot=live', 'marketplace=338811|snapshot=live']
 * aggregateField: "marketplace"
 * Return: "marketplace=1|snapshot=live";
 */
export const searchCompositeKey = (item, compositeKeys, aggregateField) => {
  const re = `${aggregateField}=${item}\\b`;
  return compositeKeys.filter((key) => key.match(re))[0];
}

/**
 * Extract the matched aggregateField attribute
 * compositeKeys: ['marketplace=1|snapshot=live', 'marketplace=338811|snapshot=live']
 * aggregateField: "marketplace"
 * Return: ["1", "338811"]
 */
export const extractFromCompositeKeys = (compositeKeys, aggregateField) => {
  return compositeKeys.map((key) => extractFromCompositeKey(key, aggregateField));
}

export const extractFromCompositeKey = (compositeKey, aggregateField) => {
  const re = `${aggregateField}=\\w*`;
  return compositeKey.match(re)[0].replace(`${aggregateField}=`, '');
}

const getPayloadValue = (requestPayload, key) => {
  const idKey = 'Id';
  if (key.endsWith(idKey)) {
    const keyWithoutId = key.slice(0, -idKey.length);
    const payload = requestPayload[keyWithoutId];
    return payload[idKey] || payload[idKey.toLowerCase()] || payload[idKey.toUpperCase()];
  }

  return requestPayload[key];
};



// WEBPACK FOOTER //
// ./src/components/reports/utils/composite_keys.js